import { UnfoldMore } from '@mui/icons-material'
import { Components, Theme } from '@mui/material'

export const MuiAutocomplete: Components<Omit<Theme, 'components'>>['MuiAutocomplete'] = {
  defaultProps: {
    // TODO - new icon
    popupIcon: <UnfoldMore />,
    slotProps: {
      // Only applies if not using a custom renderTags/renderValue
      chip: {
        size: 'small',
        variant: 'selection',
      },
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      // Not targetting slot to increase specificity
      '& .MuiAutocomplete-inputRoot.MuiInput-root .MuiAutocomplete-input': {
        padding: 0,
      },
      '& .MuiAutocomplete-inputRoot:has(.MuiAutocomplete-tag)': {
        gap: theme.spacing(1),
      },
      variants: [
        {
          props: { size: 'medium' },
          style: {
            '& .MuiAutocomplete-inputRoot': {
              paddingLeft: theme.spacing(3),
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
              // Multiselect with values - reduce padding
              '&:has(.MuiAutocomplete-tag)': {
                padding: theme.spacing(1.5, 2.5, 1.5, 1.5),
              },
              '& .MuiAutocomplete-tag': {
                minHeight: 28,
              },
            },
          },
        },
        {
          props: { size: 'small' },
          style: {
            '& .MuiAutocomplete-inputRoot': {
              paddingLeft: theme.spacing(3),
              paddingTop: theme.spacing(1.5),
              paddingBottom: theme.spacing(1.5),
              // Multiselect with values - reduce padding
              '&:has(.MuiAutocomplete-tag)': {
                padding: theme.spacing(1.5, 2.5, 1.5, 1.5),
              },
            },
          },
        },
      ],
    }),
    clearIndicator: ({ theme }) => ({
      color: theme.palette.fg.default,
      marginRight: theme.spacing(0.5),
    }),
    popupIndicator: ({ theme }) => ({
      color: theme.palette.fg.default,
      marginRight: theme.spacing(1.5),
    }),
    paper: ({ theme }) => ({
      marginTop: theme.spacing(1.5),
      padding: theme.spacing(0),
      borderRadius: theme.shape['radius-md'],
      backgroundColor: theme.palette.bg.surface.elevated,
      boxShadow: theme.customShadows.sm,
      // If we have nothing to show, don't show a border - looks like a strange autocomplete showing
      '&:empty': {
        border: 'none',
      },
    }),
    listbox: ({ theme }) => ({
      // Spacing is on the list, not on the container, so that scroll goes to the top
      padding: theme.spacing(1),
      '& .MuiAutocomplete-option': {
        borderRadius: theme.shape['radius-sm'],
        padding: theme.spacing(0.5, 2),
        minHeight: '28px',
        ...theme.typography['body-regular'],
        color: theme.palette.text.default,
        '&:hover': {
          backgroundColor: theme.palette.bg.interactive.hover,
        },
        '&:not(:last-child)': {
          marginBottom: theme.spacing(0.5),
        },
        '&[aria-disabled="true"]': {
          opacity: 0.4,
        },
      },
      // Spacing between groups — group wrappers are the direct li children
      // that aren't plain options (ungrouped options are also direct children)
      '& > li:not(:first-child):not(.MuiAutocomplete-option)': {
        paddingTop: theme.spacing(1),
      },
      '& > li:not(:last-child) .MuiAutocomplete-groupUl': {
        borderBottomStyle: 'solid',
        borderBottomWidth: theme.strokeWidth.default,
        borderColor: theme.palette.border.subtle,
        paddingBottom: theme.spacing(1),
      },
      '& .MuiAutocomplete-groupUl .MuiAutocomplete-option': {
        paddingLeft: theme.spacing(2),
      },
    }),
    groupLabel: ({ theme }) => ({
      ...theme.typography['small-regular'],
      color: theme.palette.text.subtle,
      padding: theme.spacing(1, 1.5),
      // Increase the top pad, but offset with a negative margin
      // This means that the text doesn't move up at all when sticky
      // Slightly different to Menu subheader, as there the Paper scrolls rather than the list
      paddingTop: theme.spacing(3),
      marginTop: theme.spacing(-2),
      backgroundColor: theme.palette.bg.surface.elevated,
    }),
    tag: {
      margin: 0,
    },
  },
}
